import React, { useEffect, useState } from 'react';
import getFileType from '../../helpers/getFileType';
import VideoPlayer from './VideoPlayer';

const ImageModal = ({ isOpen, onClose, images, currentIndex = 0 }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(currentIndex);

  useEffect(() => {
    setCurrentImageIndex(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => 
      prev === images.length - 1 ? 0 : prev + 1
    );
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !images || images.length === 0) return null;

  const currentMedia = images[currentImageIndex];
  const isVideo = getFileType(currentMedia) === 'video';

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
      onClick={handleBackdropClick}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-[100px] right-4 z-20 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-2 text-white hover:text-gray-300 transition-all duration-200 shadow-lg"
        aria-label="Close modal"
      >
        <svg 
          className="w-6 h-6 md:w-8 md:h-8" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M6 18L18 6M6 6l12 12" 
          />
        </svg>
      </button>
      
      {/* Alternative Text Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 left-4 z-20 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-lg px-3 py-2 text-white hover:text-gray-300 transition-all duration-200 shadow-lg text-sm font-medium"
        aria-label="Close modal"
      >
        Close
      </button>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:text-gray-300 transition-colors duration-200"
            aria-label="Previous image"
          >
            <svg 
              className="w-8 h-8 md:w-10 md:h-10" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M15 19l-7-7 7-7" 
              />
            </svg>
          </button>

          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:text-gray-300 transition-colors duration-200"
            aria-label="Next image"
          >
            <svg 
              className="w-8 h-8 md:w-10 md:h-10" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M9 5l7 7-7 7" 
              />
            </svg>
          </button>
        </>
      )}

      {/* Image Counter */}
      {images.length > 1 && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 text-white text-sm md:text-base">
          {currentImageIndex + 1} / {images.length}
        </div>
      )}

      {/* Media Content */}
      <div className="relative w-full h-full flex items-center justify-center">
        <div className="max-w-full max-h-full">
          {isVideo ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-full max-w-4xl max-h-[80vh]">
                <VideoPlayer src={currentMedia} />
              </div>
            </div>
          ) : (
            <img
              src={currentMedia}
              alt={`Product image ${currentImageIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain"
              style={{ maxWidth: '90vw', maxHeight: '80vh' }}
            />
          )}
        </div>
      </div>

      {/* Thumbnail Navigation */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
          <div className="flex space-x-2 overflow-x-auto max-w-full px-4">
            {images.map((media, index) => {
              const isCurrent = index === currentImageIndex;
              const isVideoThumb = getFileType(media) === 'video';
              
              return (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                    isCurrent 
                      ? 'border-white scale-110' 
                      : 'border-gray-400 hover:border-gray-300'
                  }`}
                >
                  {isVideoThumb ? (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <svg 
                        className="w-6 h-6 text-white" 
                        fill="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  ) : (
                    <img
                      src={media}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageModal;